import { apiClient } from "~/utils/apiClient";

const { VITE_ACCESS_CONTROLLER_API } = import.meta.env;

// v1 shares the access-controller root with legacy, so we append /v1 here instead of repointing
// the env var, which keeps the root available for calls with no v1 equivalent (resource grants)
export const ACCESS_CONTROLLER_V1 = `${VITE_ACCESS_CONTROLLER_API}/v1`;

const V1_LIST_PAGE_SIZE = 200;

interface V1PageMetadata {
  limit: number;
  offset: number;
  total_count: number;
  has_more: boolean;
}

interface V1ListResponse<T> {
  items: T[];
  metadata: V1PageMetadata;
}

// Builds the URL for a single bounded page. Exposed separately so callers can use
// the same string as a cache key, keeping the bound out of band of the fetch.
export const v1PageUrl = (url: string, limit: number): string =>
  `${url}${url.includes("?") ? "&" : "?"}limit=${limit}&offset=0`;

// Fetches one already-bounded page (see `v1PageUrl`). Typeaheads render a short
// list, so walking every page is wasted work.
export async function fetchV1PageItems<T>(url: string): Promise<T[]> {
  const page = await apiClient<V1ListResponse<T>>(url);
  return page.items;
}

// Walks limit/offset pages of a v1 list endpoint and returns every item
export async function fetchAllV1Items<T>(url: string): Promise<T[]> {
  const items: T[] = [];
  let offset = 0;
  while (true) {
    const separator = url.includes("?") ? "&" : "?";
    const page = await apiClient<V1ListResponse<T>>(
      `${url}${separator}limit=${V1_LIST_PAGE_SIZE}&offset=${offset}`
    );
    items.push(...page.items);
    if (!page.metadata.has_more || page.items.length === 0) {
      return items;
    }
    offset += page.items.length;
  }
}
